import React, { useContext } from 'react';
import PropTypes from 'prop-types';

import {
  Button,
  ButtonGroup,
  Dropdown,
  Icon,
  IconButton,
  MenuList,
  Select,
  SplitButton,
} from 'next/components/ui';
import { AndShape, OrShape, NotShape } from 'next/entities/rules';

import { UrlClause } from './UrlClause';
import { PropertiesClause } from './PropertiesClause';
import { GroupsClause } from './GroupsClause';
import { LanguagesClause } from './LanguagesClause';
import { ContentClause } from './ContentClause';
import { EventClause } from './EventClause';
import { SegmentsClause } from './SegmentsClause';
import { ChecklistsClause } from './ChecklistsClause';
import { ConditionsContext } from './ConditionsContext';
import { conditionsReducer } from './reducer';
import {
  addClause,
  addGroup,
  deleteClause,
  deleteGroup,
  negateClause,
  permitClause,
  ungroup,
  updateClause,
  updateGroup,
} from './actions';
import {
  Bracket,
  BracketWrapper,
  Child,
  Children,
  ConditionsType,
  ConditionsWrapper,
  Content,
  UpgradeBadge,
} from './styled';

const CONDITION_OPTIONS = [
  { label: 'And', value: 'and' },
  { label: 'Or', value: 'or' },
];

export const CLAUSE_TYPES = {
  url: UrlClause,
  properties: PropertiesClause,
  groups: GroupsClause,
  languages: LanguagesClause,
  content: ContentClause,
  event: EventClause,
  segments: SegmentsClause,
  checklists: ChecklistsClause,
};

export const Group = ({
  conditions,
  id,
  isProtected = false,
  isRoot = true,
  onChange,
  ...unhandledProps
}) => {
  const {
    canTargetChecklists,
    checklistOptions,
    eventOptions,
    flowOptions,
    groupOptions,
    segmentOptions,
    validClauseTypes,
  } = useContext(ConditionsContext);

  const [[type, clauses]] = Object.entries(conditions);

  const dispatch = action =>
    void onChange(conditionsReducer(conditions, action));

  const [firstSubCondition] = conditions[type];
  const [firstSubConditionType] = Object.keys(firstSubCondition);

  const hideGroupControls =
    isRoot &&
    conditions[type].length === 1 &&
    !['and', 'or'].includes(firstSubConditionType);

  const clauseTypeOptions = validClauseTypes.map(value => {
    switch (value) {
      case 'url':
        return { value, label: 'Path' };
      case 'properties':
        return { value, label: 'User Property' };
      case 'groups':
        return {
          value,
          label: 'Group Property',
          isDisabled: groupOptions.length === 0,
        };
      case 'languages':
        return { value, label: 'Language' };
      case 'content':
        return {
          value,
          label: 'Flow',
          isDisabled: flowOptions.length === 0,
        };
      case 'event':
        return {
          value,
          label: 'Event',
          isDisabled: eventOptions.length === 0,
        };
      case 'segments':
        return {
          value,
          label: 'Segment',
          isDisabled: segmentOptions.length === 0,
        };
      case 'checklists':
        return {
          value,
          label: canTargetChecklists ? (
            'Checklist'
          ) : (
            <>
              Checklist
              <UpgradeBadge label="Upgrade" icon="magic" />
            </>
          ),
          isDisabled: !(canTargetChecklists && checklistOptions.length > 0),
        };
      default:
        return { value };
    }
  });

  const handleSubConditionChange = index => changes => {
    dispatch(updateClause(index, changes));
  };

  const handleSubConditionNegated = index => isNegated => {
    if (isNegated) {
      dispatch(negateClause(index));
    } else {
      dispatch(permitClause(index));
    }
  };

  const handleSubConditionDelete = index => () => {
    if (clauses.length === 1) {
      dispatch(deleteGroup());
    } else {
      dispatch(deleteClause(index));
    }
  };

  const typeLabel = type === 'and' ? 'All' : 'Any';

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <ConditionsWrapper {...unhandledProps}>
      {!hideGroupControls && (
        <BracketWrapper>
          <Select
            isSearchable={false}
            name="type"
            portal
            value={CONDITION_OPTIONS.find(({ value }) => value === type)}
            options={CONDITION_OPTIONS}
            onChange={({ value }) => void dispatch(updateGroup(value))}
          />
          <Bracket type={type} />
        </BracketWrapper>
      )}
      <Content>
        {!hideGroupControls && (
          <ConditionsType>
            {typeLabel} of the following conditions match
          </ConditionsType>
        )}
        <Children $isParentVisible={!hideGroupControls}>
          {clauses.map((subConditions, index) => {
            const { not, ...restSubConditions } = subConditions;
            const [subCondition] = not || [restSubConditions];
            const [subConditionType] = Object.keys(subCondition);

            const conditionsKey = `${id || type}.${index}.${
              not ? 'not' : ''
            }${subConditionType}`;

            if (['and', 'or'].includes(subConditionType)) {
              return (
                <Group
                  key={conditionsKey}
                  id={conditionsKey}
                  conditions={subCondition}
                  isRoot={false}
                  isProtected={clauses.length === 1}
                  onChange={handleSubConditionChange(index)}
                />
              );
            }

            const Clause = CLAUSE_TYPES[subConditionType];

            return (
              <Child key={conditionsKey}>
                {clauseTypeOptions.length > 1 && (
                  <Select
                    value={clauseTypeOptions.find(
                      ({ value: optionType }) => optionType === subConditionType
                    )}
                    options={clauseTypeOptions}
                    onChange={({ value: newSubConditionType }) =>
                      void handleSubConditionChange(index)({
                        [newSubConditionType]: {},
                      })
                    }
                    portal
                  />
                )}
                <Clause
                  key={conditionsKey}
                  conditions={subCondition}
                  negated={!!not}
                  onChange={handleSubConditionChange(index)}
                  onChangeNegated={handleSubConditionNegated(index)}
                />
                {!hideGroupControls && (
                  <IconButton
                    kind="negative"
                    icon="trash-alt"
                    aria-label="remove"
                    disabled={(isRoot || isProtected) && clauses.length === 1}
                    onClick={handleSubConditionDelete(index)}
                  />
                )}
              </Child>
            );
          })}
        </Children>
        <ButtonGroup>
          <SplitButton kind="positive">
            <Button onClick={() => void dispatch(addClause())}>
              Add condition
            </Button>
            <Dropdown
              aria-label="Open additional options"
              name="Open additional options"
              forwardedAs={Button}
              attachment={
                <MenuList
                  options={[
                    {
                      label: 'Add condition',
                      onClick: () => void dispatch(addClause()),
                    },
                    {
                      label: 'Add condition group',
                      onClick: () => void dispatch(addGroup()),
                    },
                  ]}
                />
              }
            >
              <Icon icon="chevron-down" />
            </Dropdown>
          </SplitButton>

          {!isRoot && (
            <Button kind="secondary" onClick={() => void dispatch(ungroup())}>
              <Icon icon="unlink" /> Ungroup
            </Button>
          )}
        </ButtonGroup>
      </Content>
    </ConditionsWrapper>
  );
};

Group.propTypes = {
  id: PropTypes.string,
  conditions: PropTypes.oneOfType([
    PropTypes.exact(AndShape),
    PropTypes.exact(OrShape),
    PropTypes.exact(NotShape),
  ]),
  isRoot: PropTypes.bool,
  isProtected: PropTypes.bool,
  onChange: PropTypes.func,
};
